// Dev Mode's one gate and its shared names (docs/features/dev-mode.md). Read by the page (`import './gate.js'`) and
// by the service worker (`importScripts`), so it is a plain script with no import or export: it leaves
// `globalThis.codyDevCode` behind and nothing else.
//
// Dev Mode exists only on staging (facecheck.codymccodeface.com) and on local hosts (a local worker, a quick tunnel
// to one). On the production site — www.codymccodeface.com, app.codymccodeface.com — `allowedHost` says no, so the
// page never offers the switch and the service worker never routes a request: nothing of it runs there.
;(function (g) {
  const allowedHost = (host) => {
    host = String(host || '').toLowerCase()
    return host === 'facecheck.codymccodeface.com' || host === 'localhost' || host === '127.0.0.1' || host === '[::1]' ||
      host.endsWith('.localhost') || host.endsWith('.trycloudflare.com')
  }
  /// A path of the app as the Mac is asked for it: relative to the app root, decoded, '' → index.html. Null for
  /// anything that is not a plain path — empty, '.', '..' or hidden parts, backslashes (the Mac refuses those too).
  const appPath = (pathname, base) => {
    if (!String(pathname).startsWith(base)) return null
    let rel
    try { rel = decodeURIComponent(pathname.slice(base.length)) } catch { return null }
    if (rel === '' || rel.endsWith('/')) rel += 'index.html'
    if (rel.split('/').some((c) => c === '' || c === '.' || c === '..' || c.startsWith('.')) || rel.includes('\\') || rel.includes('\0')) return null
    return rel
  }
  // MARK: the signed file list (docs/features/dev-mode.md › Signed): the Mac signs it with its identity key — the
  // key this browser pinned when it paired (core/pairing.js) — ECDSA P-256 / SHA-256, raw r‖s, base64url, over
  // "cody-devcode-v1\0" and the list's JSON text exactly as sent. The list names every file with its SHA-256.
  const b64u = (buf) => { let s = ''; for (const b of new Uint8Array(buf)) s += String.fromCharCode(b); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') }
  const fromB64u = (s) => Uint8Array.from(atob(String(s).replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))
  const signedBytes = (text) => new TextEncoder().encode('cody-devcode-v1\0' + text)
  /// The list, read — {v, source, issued, files: {path: {size, mtime, sha256}}} — only when `signature` is the
  /// pinned `key`'s over it; null otherwise (no key, no signature, a forged or altered list, another key).
  const verifyManifest = async (key, signature, text) => {
    try {
      if (!key || !signature || typeof text !== 'string') return null
      const raw = fromB64u(signature)
      if (raw.length !== 64) return null
      const imported = await crypto.subtle.importKey('spki', fromB64u(key), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify'])
      if (!(await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, imported, raw, signedBytes(text)))) return null
      const list = JSON.parse(text)
      if (list?.v !== 'cody-devcode-v1' || !list.files || typeof list.files !== 'object') return null
      for (const f of Object.values(list.files)) if (typeof f?.sha256 !== 'string' || typeof f.size !== 'number') return null
      return list
    } catch { return null }
  }
  /// A file's SHA-256, base64url — what the list says for it.
  const sha256 = async (bytes) => b64u(await crypto.subtle.digest('SHA-256', bytes))

  g.codyDevCode = Object.assign(g.codyDevCode || {}, {
    verifyManifest, sha256, signedBytes,
    allowedHost,
    appPath,
    /// Where a dev file is kept in FILES_CACHE: its address on this site (the page writes, the worker reads).
    fileKey: (origin, base, rel) => origin + base + encodeURI(rel),
    STATE_CACHE: 'cody-devcode',          // the worker's state (one entry, STATE_PATH under the app root)
    FILES_CACHE: 'cody-devcode-files',    // the dev copy's files, as the Mac served them
    STATE_PATH: '__devcode/state',
  })
})(globalThis)
